import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell, { CartButton } from './AppShell';
import Sheet from './Sheet';
import Carousel from './Carousel';
import Reveal from './Reveal';
import useCart from '../hooks/useCart';
import {
    API_BASE, getDeliveryDates, formatCurrency, formatDateShort,
    getImageUrl, publicImg, TIME_SLOTS,
} from '../config/api';
import './MenuPage.css';

const FALLBACK_IMG = '/imgs/banh-trung-thu-thap-cam-lap-xuong.jpg';

const DEFAULT_PRODUCTS = [
    { id: 1, name: 'Bánh Nướng Thập Cẩm', category: 'Bánh nướng', filling: 'Thập cẩm', price: 55000, imageUrl: '/imgs/nuong-thap-cam.jpg', stock_status: 'stock', description: 'Vỏ nướng vàng óng truyền thống, nhân thập cẩm lạp xưởng quyện hạt dưa, mứt bí giòn bùi.' },
    { id: 3, name: 'Bánh Nướng Đậu Xanh Vani', category: 'Bánh nướng', filling: 'Đậu xanh', price: 45000, imageUrl: '/imgs/nuong-dau-xanh-vani.jpeg', stock_status: 'stock', description: 'Đậu xanh tán nhuyễn rất ít đường, hòa quyện hương vani ngọt dịu thanh tao.' },
    { id: 7, name: 'Bánh Nướng Trà Xanh', category: 'Bánh nướng', filling: 'Trà xanh', price: 48000, imageUrl: '/imgs/nuong-tra-xanh.jpeg', stock_status: 'stock', description: 'Matcha trà xanh nguyên chất thơm mát, vị đắng dịu hòa cùng vị ngọt tao nhã.' },
    { id: 5, name: 'Bánh Nướng Cốm Dừa', category: 'Bánh nướng', filling: 'Cốm dừa', price: 48000, imageUrl: '/imgs/nuong-com-dua.webp', stock_status: 'stock', description: 'Cốm xanh mùa thu kết hợp cơm dừa bùi béo ít đường, vỏ nướng thơm lừng.' },
    { id: 11, name: 'Bánh Dẻo Kem Trứng Muối', category: 'Bánh dẻo', filling: 'Trứng muối', price: 55000, imageUrl: '/imgs/deo-kem-trung-muoi.jpeg', stock_status: 'stock', description: 'Vỏ dẻo nước hoa bưởi tinh khôi ôm lấy nhân kem trứng muối béo ngậy thơm bùi.' },
    { id: 6, name: 'Bánh Dẻo Thập Cẩm', category: 'Bánh dẻo', filling: 'Thập cẩm', price: 55000, imageUrl: '/imgs/deo-thap-cam.jpg', stock_status: 'stock', description: 'Lớp vỏ dẻo thanh mát hòa quyện nhân thập cẩm lạp sườn giòn sần sật cổ truyền.' },
    { id: 10, name: 'Bánh Dẻo Cốm Dừa', category: 'Bánh dẻo', filling: 'Cốm dừa', price: 48000, imageUrl: '/imgs/deo-com-dua.jpg', stock_status: 'stock', description: 'Cốm non mùa thu dẻo mềm quyện cơm dừa nạo sợi thơm ngậy, ngọt nhẹ thanh khiết.' },
    { id: 9, name: 'Bánh Dẻo Trà Xanh', category: 'Bánh dẻo', filling: 'Trà xanh', price: 48000, imageUrl: '/imgs/deo-tra-xanh.png', stock_status: 'stock', description: 'Bánh dẻo trà xanh thanh nhiệt, vị ngọt dịu sảng khoái và hương thơm mộc tự nhiên.' },
    { id: 8, name: 'Bánh Dẻo Đậu Xanh Vani', category: 'Bánh dẻo', filling: 'Đậu xanh', price: 45000, imageUrl: '/imgs/deo-dau-xanh-vani.webp', stock_status: 'stock', description: 'Thuần khiết thanh tịnh với đậu xanh rất ít đường và hương vani tự nhiên ngọt lành.' },
    { id: 12, name: 'Bánh Dẻo Không Nhân', category: 'Bánh dẻo', filling: 'Không nhân', price: 20000, imageUrl: '/imgs/image-1788536205898-135732376.webp', stock_status: 'stock', description: 'Bánh dẻo chay truyền thống không nhân, bột nếp thơm hương hoa bưởi tinh khôi, ngọt thanh dịu nhẹ.' },
];

const CATEGORIES = ['Tất cả', 'Bánh nướng', 'Bánh dẻo'];
const FILLINGS = ['Thập cẩm', 'Đậu xanh', 'Trà xanh', 'Cốm dừa', 'Trứng muối', 'Không nhân'];

export default function MenuPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const dates = getDeliveryDates();
    const { count, total } = useCart();

    const query = new URLSearchParams(location.search);
    const savedDate = query.get('deliveryDate') || localStorage.getItem('selectedDeliveryDate');
    const [deliveryDate, setDeliveryDate] = useState(
        dates.some(d => d.iso === savedDate) ? savedDate : dates[0]?.iso
    );
    const [deliverySlot, setDeliverySlot] = useState(
        localStorage.getItem('selectedDeliverySlot') || TIME_SLOTS[0]
    );

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('Tất cả');
    const [filling, setFilling] = useState(null);
    const [keyword, setKeyword] = useState('');
    const [sort, setSort] = useState('default');

    const [dateSheet, setDateSheet] = useState(false);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/product/getProduct`);
                const list = res.data?.data;
                if (alive) setProducts(Array.isArray(list) && list.length ? list : DEFAULT_PRODUCTS);
            } catch (err) {
                console.warn('Dùng menu mặc định:', err.message);
                if (alive) setProducts(DEFAULT_PRODUCTS);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const cakes = useMemo(() => products.filter((p) => !(p.name || '').includes('Hộp')), [products]);

    const counts = useMemo(() => ({
        'Tất cả': cakes.length,
        'Bánh nướng': cakes.filter((p) => (p.category || '').includes('nướng')).length,
        'Bánh dẻo': cakes.filter((p) => (p.category || '').includes('dẻo')).length,
    }), [cakes]);

    const visible = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        return cakes
            .filter((p) => {
                if (category === 'Bánh nướng' && !(p.category || '').includes('nướng')) return false;
                if (category === 'Bánh dẻo' && !(p.category || '').includes('dẻo')) return false;
                if (filling && !`${p.filling || ''} ${p.name || ''}`.toLowerCase().includes(filling.toLowerCase())) return false;
                if (kw && !`${p.name || ''} ${p.description || ''}`.toLowerCase().includes(kw)) return false;
                return true;
            })
            .sort((a, b) => {
                if (sort === 'asc') return (a.price || 0) - (b.price || 0);
                if (sort === 'desc') return (b.price || 0) - (a.price || 0);
                return 0;
            });
    }, [cakes, category, filling, keyword, sort]);

    const saveDate = () => {
        localStorage.setItem('selectedDeliveryDate', deliveryDate);
        localStorage.setItem('selectedDeliverySlot', deliverySlot);
        setDateSheet(false);
    };

    const pickBox = (boxId) => {
        const cakeId = detail?.id;
        setDetail(null);
        navigate(`/gift-box?box=${boxId}${cakeId ? `&cakeId=${cakeId}` : ''}`);
    };

    const resetFilters = () => {
        setCategory('Tất cả');
        setFilling(null);
        setKeyword('');
        setSort('default');
    };

    return (
        <AppShell
            title="Menu bánh"
            subtitle={`${visible.length} vị đang nhận đặt`}
            right={<CartButton />}
            crumbs={[{ label: 'Menu bánh' }]}
            className="no-desk-bar"
            actionBar={count > 0 ? (
                <>
                    <div className="bar-total">
                        <div className="bar-total-label">{count} món trong giỏ</div>
                        <div className="bar-total-value tick" key={total}>{formatCurrency(total)}</div>
                    </div>
                    <button type="button" className="btn btn-primary" onClick={() => navigate('/cart')}>
                        Xem giỏ hàng
                    </button>
                </>
            ) : null}
        >
            <div className="shell menu-layout">
                {/* Filters — a sidebar on desktop, the usual stack on phones */}
                <aside className="menu-side">
                    <div className="side-label">Ngày nhận</div>
                    <button type="button" className="deliv-bar" onClick={() => setDateSheet(true)}>
                    <span className="deliv-icon"><i className="fa-solid fa-truck" /></span>
                    <span className="deliv-text">
                        <span className="deliv-label">Giao ngày</span>
                        <span className="deliv-value">{formatDateShort(deliveryDate)} · {deliverySlot}</span>
                    </span>
                    <span className="deliv-change">Đổi</span>
                </button>

                    <div className="side-label">Tìm kiếm</div>
                    <div className="search-row">
                    <div className="search-box">
                        <i className="fa-solid fa-magnifying-glass" />
                        <input
                            type="search"
                            placeholder="Tìm vị bánh..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            aria-label="Tìm vị bánh"
                        />
                        {keyword && (
                            <button type="button" className="search-clear" onClick={() => setKeyword('')} aria-label="Xoá tìm kiếm">
                                <i className="fa-solid fa-xmark" />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        className={`sort-btn ${sort !== 'default' ? 'on' : ''}`}
                        onClick={() => setSort(sort === 'default' ? 'asc' : sort === 'asc' ? 'desc' : 'default')}
                        aria-label="Sắp xếp theo giá"
                    >
                        <i className={sort === 'desc' ? 'fa-solid fa-arrow-down-wide-short' : 'fa-solid fa-arrow-up-short-wide'} />
                    </button>
                </div>

                    <div className="side-label">Dòng bánh</div>
                    <div className="seg menu-seg">
                    {CATEGORIES.map((c) => (
                        <button key={c} type="button" className={category === c ? 'on' : ''} onClick={() => setCategory(c)}>
                            {c} <span className="seg-count">{counts[c] || 0}</span>
                        </button>
                    ))}
                </div>

                    <div className="side-label">Nhân bánh</div>
                    <Carousel className="fill-row" bleed arrows ariaLabel="Lọc theo nhân bánh">
                    {FILLINGS.map((f) => (
                        <button
                            key={f}
                            type="button"
                            className={`chip ${filling === f ? 'on' : ''}`}
                            onClick={() => setFilling(filling === f ? null : f)}
                        >
                            {f}
                        </button>
                    ))}
                    </Carousel>

                    <button type="button" className="side-reset" onClick={resetFilters}>
                        <i className="fa-solid fa-rotate-left" /> Đặt lại bộ lọc
                    </button>
                </aside>

                {/* Products */}
                <div className="menu-main">
                    <div className="menu-toolbar">
                        <span className="menu-count"><strong>{visible.length}</strong> vị bánh</span>
                        <span className="menu-hint">Chạm vào bánh để chọn hộp quà</span>
                    </div>
                {loading ? (
                    <div className="cake-grid">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="cake-card">
                                <div className="skeleton" style={{ aspectRatio: '1 / 1' }} />
                                <div className="cake-body">
                                    <div className="skeleton" style={{ height: 13, width: '85%', marginBottom: 7 }} />
                                    <div className="skeleton" style={{ height: 11, width: '60%' }} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : visible.length === 0 ? (
                    <div className="empty">
                        <div className="empty-icon"><i className="fa-solid fa-magnifying-glass" /></div>
                        <h3>Không tìm thấy vị bánh</h3>
                        <p>Không có bánh nào khớp với bộ lọc hiện tại.</p>
                        <button type="button" className="btn btn-outline" onClick={resetFilters}>Đặt lại bộ lọc</button>
                    </div>
                ) : (
                    <Reveal stagger className="cake-grid" key={`${category}-${filling}-${keyword}-${sort}`}>
                        {visible.map((p) => {
                            const out = p.stock_status === 'outstock';
                            const low = p.stock_status === 'low';
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    className={`cake-card ${out ? 'out' : ''}`}
                                    onClick={() => !out && setDetail(p)}
                                >
                                    <div className="cake-thumb">
                                        <img
                                            src={getImageUrl(p.imageUrl)}
                                            alt={p.name}
                                            loading="lazy"
                                            className="img-fade"
                                            onLoad={(e) => e.currentTarget.classList.add('loaded')}
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = publicImg(FALLBACK_IMG);
                                                e.currentTarget.classList.add('loaded');
                                            }}
                                        />
                                        <span className={`cake-stock badge ${out ? 'cancel' : low ? 'wait' : 'paid'}`}>
                                            {out ? 'Hết lịch' : low ? 'Sắp kín' : 'Còn nhận'}
                                        </span>
                                    </div>

                                    <div className="cake-body">
                                        <div className="cake-kind">{p.category || 'Bánh nướng'}</div>
                                        <div className="cake-name clamp-2">{p.name}</div>
                                        <div className="cake-foot">
                                            <div>
                                                <div className="t-price cake-price">{formatCurrency(p.price)}</div>
                                                <div className="cake-unit">150g · chưa trứng</div>
                                            </div>
                                            <span className="cake-add"><i className="fa-solid fa-plus" /></span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                        </Reveal>
                )}
                </div>
            </div>

            {/* Delivery date sheet */}
            <Sheet
                open={dateSheet}
                onClose={() => setDateSheet(false)}
                subtitle="Đặt trước 48 giờ"
                title="Ngày và giờ nhận bánh"
                footer={<button type="button" className="btn btn-primary btn-block" onClick={saveDate}>Xác nhận lịch giao</button>}
            >
                <div className="dates-2-grid" style={{ marginBottom: 16 }}>
                    {dates.map((d) => (
                        <button
                            key={d.iso}
                            type="button"
                            className={`daychip-wide ${deliveryDate === d.iso ? 'on' : ''} ${d.disabled ? 'off' : ''}`}
                            onClick={() => !d.disabled && setDeliveryDate(d.iso)}
                        >
                            <div className="daychip-dow">{d.dowFull || (d.dow === 'T2' ? 'Thứ Hai' : 'Thứ Sáu')}</div>
                            <div className="daychip-date">{d.day} Tháng 09</div>
                            <div className="daychip-note">{deliveryDate === d.iso ? 'Đang chọn' : d.note}</div>
                        </button>
                    ))}
                </div>

                <div className="sheet-label">Khung giờ nhận</div>
                <div className="slot-grid">
                    {TIME_SLOTS.map((s) => (
                        <button
                            key={s}
                            type="button"
                            className={`slot ${deliverySlot === s ? 'on' : ''}`}
                            onClick={() => setDeliverySlot(s)}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </Sheet>

            {/* Product detail sheet */}
            <Sheet
                open={!!detail}
                onClose={() => setDetail(null)}
                subtitle={detail?.category || 'Bánh handmade'}
                title={detail?.name}
            >
                {detail && (
                    <>
                        <div className="detail-media">
                            <img
                                src={getImageUrl(detail.imageUrl)}
                                alt={detail.name}
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = publicImg(FALLBACK_IMG); }}
                            />
                        </div>

                        <p className="detail-desc">{detail.description || 'Hương vị truyền thống hảo hạng, sên tay mỗi ngày.'}</p>

                        <div className="detail-specs">
                            <div className="spec"><span>150g</span><small>chuẩn</small></div>
                            <div className="spec"><span>180g</span><small>+10.000 ₫</small></div>
                            <div className="spec"><span>Trứng muối</span><small>+7.000 ₫</small></div>
                        </div>

                        <div className="detail-price-row">
                            <span className="t-muted">Giá từ</span>
                            <span className="t-price detail-price">{formatCurrency(detail.price)}</span>
                        </div>

                        <div className="sheet-label">Chọn hình thức mua bánh</div>
                        <div className="box-pick box-pick-3">
                            <button type="button" className="box-pick-card" onClick={() => pickBox('single')}>
                                <img
                                    src={getImageUrl(detail.imageUrl)}
                                    alt={detail.name}
                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = publicImg(FALLBACK_IMG); }}
                                />
                                <div className="box-pick-name">Mua Lẻ 1 Cái</div>
                                <div className="box-pick-sub">Không cần hộp</div>
                            </button>
                            <button type="button" className="box-pick-card" onClick={() => pickBox('box2')}>
                                <img src={publicImg('/imgs/hop-2-banh.webp')} alt="Hộp 2 bánh" />
                                <div className="box-pick-name">Hộp 2 Bánh</div>
                                <div className="box-pick-sub">Vỏ hộp &amp; túi 0 ₫</div>
                            </button>
                            <button type="button" className="box-pick-card" onClick={() => pickBox('box4')}>
                                <span className="box-pick-tag">Phổ biến</span>
                                <img src={publicImg('/imgs/hop-4-banh.webp')} alt="Hộp 4 bánh" />
                                <div className="box-pick-name">Hộp 4 Bánh</div>
                                <div className="box-pick-sub">Kèm túi sen cá</div>
                            </button>
                        </div>
                    </>
                )}
            </Sheet>
        </AppShell>
    );
}
